// One status vocabulary for the whole app: word → label + tone.
//
// The same word used to get different colours on different screens ('Approved' blue here, green
// there; 'Pending' an amber pill but a red chart slice). A tone is a meaning, not a colour choice:
//   success  done / good / paid        warning  waiting / due soon / needs a look
//   danger   failed / overdue / risk   info     scheduled / in flight
//   neutral  inactive / draft / closed
// Plan Management keeps its own invoice pipeline map (pages/PlanManagement/pmStatus.js), which follows
// the owner's specification.

export const STATUS = {
  // general records
  active:        { label: 'Active', tone: 'success' },
  inactive:      { label: 'Inactive', tone: 'neutral' },
  archived:      { label: 'Archived', tone: 'neutral' },
  draft:         { label: 'Draft', tone: 'neutral' },
  pending:       { label: 'Pending', tone: 'warning' },
  approved:      { label: 'Approved', tone: 'success' },
  rejected:      { label: 'Rejected', tone: 'danger' },
  declined:      { label: 'Declined', tone: 'neutral' },
  cancelled:     { label: 'Cancelled', tone: 'neutral' },
  canceled:      { label: 'Cancelled', tone: 'neutral' },
  cancelled_with_charge: { label: 'Cancelled (charged)', tone: 'neutral' },
  // shifts and work
  booked:        { label: 'Booked', tone: 'info' },
  scheduled:     { label: 'Scheduled', tone: 'info' },
  published:     { label: 'Published', tone: 'info' },
  planned:       { label: 'Planned', tone: 'neutral' },
  in_progress:   { label: 'In progress', tone: 'info' },
  completed:     { label: 'Completed', tone: 'success' },
  done:          { label: 'Done', tone: 'success' },
  not_required:  { label: 'Not required', tone: 'neutral' },
  absent:        { label: 'Absent', tone: 'neutral' },
  // money
  invoiced:      { label: 'Invoiced', tone: 'success' },
  uninvoiced:    { label: 'Not invoiced', tone: 'warning' },
  sent:          { label: 'Sent', tone: 'info' },
  paid:          { label: 'Paid', tone: 'success' },
  unpaid:        { label: 'Unpaid', tone: 'warning' },
  partially_paid:{ label: 'Part paid', tone: 'warning' },
  overdue:       { label: 'Overdue', tone: 'danger' },
  void:          { label: 'Void', tone: 'neutral' },
  voided:        { label: 'Void', tone: 'neutral' },
  submitted:     { label: 'Submitted', tone: 'info' },
  failed:        { label: 'Failed', tone: 'danger' },
  // cases, incidents, tasks
  open:          { label: 'Open', tone: 'warning' },
  new:           { label: 'New', tone: 'info' },
  investigating: { label: 'Investigating', tone: 'info' },
  escalated:     { label: 'Escalated', tone: 'danger' },
  resolved:      { label: 'Resolved', tone: 'success' },
  closed:        { label: 'Closed', tone: 'neutral' },
  on_hold:       { label: 'On hold', tone: 'warning' },
  // documents and compliance
  valid:         { label: 'Valid', tone: 'success' },
  current:       { label: 'Current', tone: 'success' },
  expiring:      { label: 'Expiring soon', tone: 'warning' },
  expired:       { label: 'Expired', tone: 'danger' },
  missing:       { label: 'Missing', tone: 'danger' },
  verified:      { label: 'Verified', tone: 'success' },
  unverified:    { label: 'Not verified', tone: 'warning' },
  signed:        { label: 'Signed', tone: 'success' },
  unsigned:      { label: 'Awaiting signature', tone: 'warning' },
  // medication outcomes
  given:         { label: 'Given', tone: 'success' },
  refused:       { label: 'Refused', tone: 'danger' },
  missed:        { label: 'Missed', tone: 'danger' },
  held:          { label: 'Held', tone: 'warning' },
  not_available: { label: 'Not available', tone: 'warning' },
  prn_not_required: { label: 'Not required (PRN)', tone: 'neutral' },
  // leads
  hot:           { label: 'Hot', tone: 'danger' },
  warm:          { label: 'Warm', tone: 'warning' },
  cold:          { label: 'Cold', tone: 'neutral' },
  contacted:     { label: 'Contacted', tone: 'info' },
  converted:     { label: 'Converted', tone: 'success' },
  // reports
  expiring_soon: { label: 'Expiring soon', tone: 'warning' },
  complete:      { label: 'Complete', tone: 'success' },
  not_started:   { label: 'Not started', tone: 'neutral' },
  reimbursed:    { label: 'Reimbursed', tone: 'success' },
  review:        { label: 'Review', tone: 'warning' },
  administered:  { label: 'Administered', tone: 'success' },
};

export const PRIORITY = {
  critical: { label: 'Critical', tone: 'danger' },
  urgent:   { label: 'Urgent', tone: 'danger' },
  high:     { label: 'High', tone: 'danger' },
  medium:   { label: 'Medium', tone: 'warning' },
  normal:   { label: 'Normal', tone: 'neutral' },
  low:      { label: 'Low', tone: 'neutral' },
};

// Badge classes per tone (index.css badge-* utilities).
export const TONE_BADGE = {
  success: 'badge-green',
  warning: 'badge-yellow',
  danger:  'badge-red',
  info:    'badge-blue',
  neutral: 'badge-gray',
};

// Solid colours per tone, for chart slices and dots: the same meaning as the badges.
export const TONE_HEX = {
  success: '#10b981',
  warning: '#f59e0b',
  danger:  '#ef4444',
  info:    '#6366f1',
  neutral: '#94a3b8',
};

const humanise = (s) => {
  const t = String(s || '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

export const statusOf = (code, map = STATUS) => {
  const key = String(code || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return map[key] || { label: humanise(code) || '—', tone: 'neutral' };
};
export const statusLabel = (code) => statusOf(code).label;
export const statusTone = (code) => statusOf(code).tone;
export const statusColor = (code) => TONE_HEX[statusOf(code).tone];
